// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from "jwt-decode";


export interface User {
  username: string;
  firstName: string;
  lastName: string;
  // add more properties as needed
}

interface JwtPayload {
  exp: number;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  login: (token: string, refreshToken: string, userData: User) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  login: () => {},
  logout: () => {},
  refreshToken: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  // Helper function to check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.exp < Date.now() / 1000;
    } catch (error) {
      return true;
    }
  };

  // Intenta refrescar el access token de forma silenciosa al cargar la página.
  // IMPORTANTE: la sesión es PERSISTENTE en el dispositivo. Si el refresh falla
  // NO cerramos sesión: conservamos el token guardado para no pedir contraseña.
  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      // Sin refresh token: mantenemos la sesión actual (no cerramos sesión).
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });
      if (!res.ok) {
        throw new Error("Unable to refresh token");
      }
      const data = await res.json();
      // Assume your refresh endpoint returns the new access token (and optionally updated user data)
      localStorage.setItem("token", data.token);
      // Optionally update user data if provided:
      if (data.user) {
        setUser(data.user);
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      setIsLoggedIn(true);
    } catch (error) {
      // No cerramos sesión si falla el refresh: la sesión persiste en el dispositivo.
      console.error("Error refreshing token:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      // 1) Set user from local storage immediately
      setUser(JSON.parse(storedUser));
      setIsLoggedIn(true);

      // 2) Now attempt refresh in the background
      refreshToken();
    }
  }, []);


  const login = (token: string, refresh: string, userData: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsLoggedIn(true);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUser(null);
    window.location.href = '/inicio-sesion'; // Redirect to login page
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, login, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};
